import { useEffect, useMemo, useState } from "react";
import { Button, Pagination, Table, Tag, Tooltip } from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { Ban, CircleCheck, Pencil, Plus, Trash2 } from "lucide-react";

import {
  resourcePointTicketResourceTypeOptions,
  resourcePointTravelerRoleOptions,
} from "../config";
import { useResourcePointSkuQuery } from "../runtime";
import { formatResourcePointDateTime } from "../service";
import type { ResourcePointSkuItem, ResourcePointSpuItem } from "../types";

type ResourcePointSkuTableProps = {
  spu: ResourcePointSpuItem;
  canManage: boolean;
  canDelete: boolean;
  onCreate: (spu: ResourcePointSpuItem) => void;
  onEdit: (spu: ResourcePointSpuItem, sku: ResourcePointSkuItem) => void;
  onDelete: (spu: ResourcePointSpuItem, sku: ResourcePointSkuItem) => void;
  onToggleDisabled: (spu: ResourcePointSpuItem, sku: ResourcePointSkuItem) => void;
  togglePendingSkuIds: ReadonlySet<string>;
  errorMessage: string;
};

const SKU_PAGE_SIZE = 10;

/** SKU 管理表，只拥有自身的按需查询。 */
export function ResourcePointSkuTable({
  spu,
  canManage,
  canDelete,
  onCreate,
  onEdit,
  onDelete,
  onToggleDisabled,
  togglePendingSkuIds,
  errorMessage,
}: ResourcePointSkuTableProps) {
  const query = useResourcePointSkuQuery(spu.resourcePointSpuId, true);
  const [page, setPage] = useState(1);
  const lastPage = Math.max(1, Math.ceil(query.items.length / SKU_PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const pageItems = useMemo(
    () =>
      query.items.slice(
        (currentPage - 1) * SKU_PAGE_SIZE,
        currentPage * SKU_PAGE_SIZE,
      ),
    [currentPage, query.items],
  );

  useEffect(() => {
    setPage(1);
  }, [spu.resourcePointSpuId]);

  useEffect(() => {
    setPage((current) => Math.min(current, lastPage));
  }, [lastPage]);

  const columns: TableColumnProps<ResourcePointSkuItem>[] = [
    { title: "SKU 名称", dataIndex: "skuName", width: 220, ellipsis: true },
    {
      title: "票务类型",
      dataIndex: "ticketResourceType",
      width: 130,
      render: (value) => optionLabel(resourcePointTicketResourceTypeOptions, value),
    },
    {
      title: "旅客角色",
      dataIndex: "travelerRole",
      width: 100,
      render: (value) => optionLabel(resourcePointTravelerRoleOptions, value),
    },
    { title: "结算单位", dataIndex: "settlementUnit", width: 90 },
    {
      title: "预约",
      dataIndex: "reservationRequired",
      width: 90,
      render: (value) => (value ? "需要预约" : "无需预约"),
    },
    {
      title: "团体人数",
      width: 120,
      render: (_, record) => formatGroupSize(record.minGroupSize, record.maxGroupSize),
    },
    {
      title: "状态",
      width: 100,
      render: (_, record) => (
        <Tag color={record.isDisabled || record.status !== "active" ? "gray" : "green"}>
          {record.isDisabled ? "停用" : record.status === "active" ? "有效" : "无效"}
        </Tag>
      ),
    },
    {
      title: "更新时间",
      width: 170,
      render: (_, record) => formatResourcePointDateTime(record.updatedAt),
    },
    {
      title: "操作",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <div className="resource-point-table-actions">
          {canManage ? (
            <>
              <Tooltip content={`编辑 ${record.skuName}`}>
                <Button
                  type="text"
                  size="mini"
                  aria-label={`编辑 ${record.skuName}`}
                  icon={<Pencil size={15} />}
                  onClick={() => onEdit(spu, record)}
                />
              </Tooltip>
              <Tooltip content={`${record.isDisabled ? "启用" : "禁用"} ${record.skuName}`}>
                <Button
                  type="text"
                  size="mini"
                  aria-label={`${record.isDisabled ? "启用" : "禁用"} ${record.skuName}`}
                  disabled={togglePendingSkuIds.has(record.resourcePointSkuId)}
                  loading={togglePendingSkuIds.has(record.resourcePointSkuId)}
                  icon={record.isDisabled ? <CircleCheck size={15} /> : <Ban size={15} />}
                  onClick={() => onToggleDisabled(spu, record)}
                />
              </Tooltip>
            </>
          ) : null}
          {canDelete ? (
            <Tooltip content={`删除 ${record.skuName}`}>
              <Button
                type="text"
                size="mini"
                status="danger"
                aria-label={`删除 ${record.skuName}`}
                icon={<Trash2 size={15} />}
                onClick={() => onDelete(spu, record)}
              />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <section className="resource-point-sku" aria-label={`${spu.pointName} SKU`}>
      <div className="resource-point-sku__toolbar">
        <strong>{spu.pointName} · SKU</strong>
        {canManage ? (
          <Button
            size="small"
            type="primary"
            icon={<Plus size={15} />}
            aria-label={`新增 ${spu.pointName} SKU`}
            onClick={() => onCreate(spu)}
          >
            新增 SKU
          </Button>
        ) : null}
      </div>
      {query.loading ? <div className="resource-point-state">正在加载 SKU...</div> : null}
      {query.errorMessage ? <div className="resource-point-error" role="alert">{query.errorMessage}</div> : null}
      {errorMessage ? <div className="resource-point-error" role="alert">{errorMessage}</div> : null}
      <div
        className="resource-point-sku__table-viewport"
        data-testid="resource-point-sku-table-viewport"
      >
        <Table<ResourcePointSkuItem>
          rowKey="resourcePointSkuId"
          size="mini"
          border={{ wrapper: true, cell: false }}
          loading={query.loading || query.fetching}
          columns={columns}
          data={pageItems}
          pagination={false}
          scroll={{ x: 1150 }}
          tableLayoutFixed
          noDataElement="该考察点暂无 SKU"
        />
      </div>
      <div
        className="resource-point-sku__pagination"
        data-testid="resource-point-sku-pagination"
      >
        <Pagination
          current={currentPage}
          pageSize={SKU_PAGE_SIZE}
          total={query.items.length}
          showTotal
          onChange={setPage}
        />
      </div>
    </section>
  );
}

/** 返回枚举配置的中文标签，未知值保留原文。 */
function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
) {
  return options.find((option) => option.value === value)?.label ?? value;
}

/** 格式化团体人数上下限。 */
function formatGroupSize(min?: number, max?: number) {
  if (min === undefined && max === undefined) return "不限";
  if (min === undefined) return `不超过 ${max} 人`;
  if (max === undefined) return `至少 ${min} 人`;
  return `${min}-${max} 人`;
}
